import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import DriverService from "app/data/drivers/driversService";
import CityDeliveryDiaptchBoardService from "app/data/city-delivery/dispatch-board/cityDeliveryDiaptchBoardService";
import PickupService from "app/data/pickup/pickupService";
import { PickupStatus } from "app/data/common/route";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { DriverDutyDetail, DriverDutyStatus } from "app/data/drivers/models";
import {
  DispatchManifest,
  DispatchManifestStopDetailProbill,
  DispatchManifestStopDetailStatus,
  ProbillStatus,
} from "app/data/city-delivery/dispatch-board/models";
import { PickupsResponseContentModel } from "app/data/pickup/models";
import type { FreightItemForDisplay } from "../../../common/pickup-details/pickupFreightItemsBlock";

const driverService = DriverService.getInstance();
const dispatchBoardService = CityDeliveryDiaptchBoardService.getInstance();
const pickupService = PickupService.getInstance();

const MAX_PICKUP_PAGES = 200;

export type ManifestProbillRow = {
  id: string;
  probillNumber: number;
  probillStatus: ProbillStatus;
  pieces: number;
  weight: number;
  assignedRelative: string;
  consigneeName: string;
  consigneeNumber: number;
};

export type ManifestStopBlock = {
  kind: "manifest_stop";
  id: string;
  manifestNumber: number;
  stopNumber: number;
  address: string;
  stopStatus: DispatchManifestStopDetailStatus;
  probills: ManifestProbillRow[];
  deliveriesCount: number;
  stopProgressLabel: string;
};

export type PickupInGroupRow = {
  id: string;
  pickupNumber: string;
  probillNumber: number | null;
  bolNumber?: string;
  poNumber?: string;
  shipperName: string;
  assignedRelative: string;
  status: string;
  pieces: number;
  weight: number;
  timeRange: string;
  /** Schedule fields for At Risk / Late tab filtering */
  pickupDate: string;
  readyTime: string;
  closeTime: string;
  /** Units (items) with package type ROLL */
  rollsUnits: number;
  /** Units (items) with package type PALLET */
  palletsUnits: number;
  /** Total weight of all items (lbs) */
  totalItemsWeight: number;
  /** Date plus ready–close times */
  pickupWindowLine: string;
  /** Phone / email (not including company name) */
  shipperContactLine: string;
  consigneeName: string;
  consigneeAddressLine: string;
  consigneeContactLine: string;
  itemLines: FreightItemForDisplay[];
};

export type PickupGroupBlock = {
  kind: "pickup_group";
  id: string;
  shipperAddress: string;
  /** List id `driver-{id}` for map ↔ list sync */
  driverListId: string;
  pickups: PickupInGroupRow[];
  totalPickups: number;
  completedPickups: number;
  /** Aggregate status for the shipper group (mirrors manifest stop status semantics) */
  groupStopStatus: DispatchManifestStopDetailStatus;
  shipperLat?: number;
  shipperLng?: number;
};

export type DriverColumnItem = ManifestStopBlock | PickupGroupBlock;

export type PickupAssignmentDriverGroup = {
  id: string;
  name: string;
  driverId: string;
  stops: DriverColumnItem[];
  numberOfPickups: number;
  numberOfDeliveries: number;
  /** Deliveries completed (manifest probills marked delivered). */
  deliveriesCompleted: number;
  /** Pickups completed (assigned pickup rows with status COMPLETED). */
  pickupsCompleted: number;
  trailerLabel: string;
  manifestLabel: string;
  driverStatus: string;
  progressLabel: string;
  lastLocationLine: string;
  /** Primary dispatched manifest for map route (manifest tracking API). */
  primaryManifestNumber: number | null;
  /** Live driver GPS when available. */
  driverLocation: {
    lat: number;
    lng: number;
    formattedAddress?: string;
    timestamp?: string;
  } | null;
};

/** Aliases for consumers that match pickup assignment UI naming */
export type DriverGroup = PickupAssignmentDriverGroup;
export type Stop = DriverColumnItem;

const formatTime = (time: string) => {
  if (!time || time.trim() === "") return "—";
  const d = new Date(`1970-01-01T${time}`);
  if (Number.isNaN(d.getTime())) return time;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function formatRelative(iso: string | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  if (!m.isValid()) return "—";
  return m.fromNow();
}

function buildTrailerLabel(primary: DispatchManifest | undefined, detail: DriverDutyDetail): string {
  const candidates = [primary?.tractorNumber, primary?.trailerNumber, detail.tractorNumber];
  for (const raw of candidates) {
    if (raw == null || String(raw).trim() === "") continue;
    const s = String(raw).replace(/^TL-?/i, "").trim();
    return `TL-${s}`;
  }
  return "—";
}

function buildManifestLabel(primary: DispatchManifest | undefined): string {
  if (!primary) return "—";
  return `MF-${primary.manifestNumber}`;
}

function buildLastLocationLine(detail: DriverDutyDetail): string {
  const loc = detail.driverLocation;
  if (!loc?.formattedAddress) return "—";
  const rel = loc.timestamp ? formatRelative(loc.timestamp) : "";
  return rel ? `${loc.formattedAddress} · ${rel}` : loc.formattedAddress;
}

function mapDutyStatusToUi(detail: DriverDutyDetail): string {
  const duty = detail.duty;
  if (!duty?.status) {
    return detail.tractorNumber ? "AVAILABLE" : "OFF_DUTY";
  }
  switch (duty.status) {
    case DriverDutyStatus.offDuty:
    case DriverDutyStatus.sleeperBed:
    case DriverDutyStatus.personalConveyance:
      return "OFF_DUTY";
    case DriverDutyStatus.onDuty:
    case DriverDutyStatus.driving:
    case DriverDutyStatus.yardMove:
      return "ON_DUTY";
    default:
      return "ON_DUTY";
  }
}

function formatShipperAddress(model: PickupsResponseContentModel): string {
  const a = model.shipper?.address;
  if (!a) return "—";
  return `${a.address1}, ${a.city}, ${a.state} ${a.postalCode}`.trim();
}

function probillRowsFromStop(
  probills: DispatchManifestStopDetailProbill[],
  manifest: DispatchManifest,
  stopNumber: number
): ManifestProbillRow[] {
  const dispatchedRel = formatRelative(manifest.dispatchedAt);
  return (probills ?? []).map((pb, pi) => ({
    id: `pb-${manifest.manifestNumber}-${stopNumber}-${pb.probillNumber}-${pi}`,
    probillNumber: pb.probillNumber,
    probillStatus: pb.probillStatus,
    pieces: pb.totalPieces ?? 0,
    weight: pb.totalWeight ?? 0,
    assignedRelative: dispatchedRel,
    consigneeName: pb.consigneeName ?? "—",
    consigneeNumber: pb.consigneeNumber ?? 0,
  }));
}

function stopProgressFromProbills(probills: ManifestProbillRow[]): string {
  const total = probills.length;
  if (total === 0) return "—";
  const delivered = probills.filter((p) => p.probillStatus === ProbillStatus.DELIVERED).length;
  return `${delivered}/${total} done`;
}

function joinContactParts(parts: (string | undefined | null)[]): string {
  const s = parts
    .map((x) => {
      if (x == null) return null;
      const t = String(x).trim();
      if (t === "" || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") {
        return null;
      }
      return t;
    })
    .filter((x): x is string => Boolean(x));
  return s.length ? s.join(" · ") : "—";
}

function aggregateRollsPalletsFromItemsList(
  items: PickupsResponseContentModel["itemsList"] | undefined
): { rolls: number; pallets: number } {
  let rolls = 0;
  let pallets = 0;
  for (const it of items ?? []) {
    if (it.deleted) continue;
    const u = Number(it.units ?? 0) || 0;
    const pt = (it.packageType ?? "").toUpperCase();
    if (pt === "ROLL") rolls += u;
    else if (pt === "PALLET") pallets += u;
  }
  return { rolls, pallets };
}

function sanitizeAddrToken(v: string | undefined): string {
  if (v == null) return "";
  const t = String(v).trim();
  if (t.toLowerCase() === "null" || t.toLowerCase() === "undefined") return "";
  return t;
}

function formatConsigneeAddressFromResponse(row: PickupsResponseContentModel): string {
  const a = row.consignee?.address;
  if (!a) return "—";
  const p1 = sanitizeAddrToken(a.address1);
  const city = sanitizeAddrToken(a.city);
  const state = sanitizeAddrToken(a.state);
  const zip = sanitizeAddrToken(a.postalCode);
  const line = [p1, city, state, zip].filter(Boolean).join(", ");
  if (!line) return "—";
  const addl = sanitizeAddrToken(a.additionalAddressLine);
  return addl ? `${line} (${addl})` : line;
}

function formatPickupWindowLineFromResponse(row: PickupsResponseContentModel): string {
  const p = row.pickup;
  const datePart =
    p.date && moment(p.date).isValid() ? moment(p.date).format("MMM D, YYYY") : "—";
  return `${datePart} · ${formatTime(p.readyTime)} – ${formatTime(p.closeTime)}`;
}

function shipperContactFromResponse(row: PickupsResponseContentModel): string {
  const pr = row.pickupFullData?.pickupRequest;
  if (!pr) return "—";
  return joinContactParts([pr.shipperMobilePhone, pr.shipperEmail]);
}

function consigneeContactFromResponse(row: PickupsResponseContentModel): string {
  const pr = row.pickupFullData?.pickupRequest;
  return joinContactParts([pr?.consigneePhone, pr?.consigneeEmail]);
}

function numOrUndef(v: unknown): number | undefined {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function itemLinesFromResponse(row: PickupsResponseContentModel): FreightItemForDisplay[] {
  return (row.itemsList ?? [])
    .filter((it) => !it.deleted)
    .map((it) => ({
      packageType: it.packageType,
      units: numOrUndef(it.units) ?? 0,
      yards: numOrUndef(it.yards),
      weight: numOrUndef(it.weight) ?? 0,
      length: it.length,
      width: it.width,
      height: it.height,
      diameter: it.diameter,
      ratingClass: it.ratingClass,
      commodityCodeDescription: it.commodityCodeDescription,
      notes: it.notes,
      sidemark: it.sidemark,
      description: it.description,
    }));
}

function sanitizeDisplayName(v: string | undefined | null): string {
  if (v == null) return "—";
  const t = String(v).trim();
  if (t === "" || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") return "—";
  return t;
}

function pickupToGroupRow(row: PickupsResponseContentModel): PickupInGroupRow {
  const p = row.pickup;
  const pr = row.pickupFullData?.pickupRequest;
  const probill = pr?.probillNumber;
  const { rolls, pallets } = aggregateRollsPalletsFromItemsList(row.itemsList);
  const itemsWeight = Number(row.items?.weight ?? 0) || 0;
  return {
    id: p.pickupId,
    pickupNumber: p.pickupNumber || p.pickupId,
    probillNumber:
      probill != null && probill > 0 ? probill : null,
    bolNumber: pr?.bolNumber,
    poNumber: pr?.poNumber,
    shipperName: sanitizeDisplayName(row.shipper?.name || pr?.shipperName || ""),
    assignedRelative: formatRelative(pr?.dateUpdated || pr?.dateCreated || p.date),
    status: p.pickupStatus,
    pieces: row.items?.count ?? 0,
    weight: itemsWeight,
    timeRange: `${formatTime(p.readyTime)} - ${formatTime(p.closeTime)}`,
    pickupDate: p.date,
    readyTime: p.readyTime,
    closeTime: p.closeTime,
    rollsUnits: rolls,
    palletsUnits: pallets,
    totalItemsWeight: itemsWeight,
    pickupWindowLine: formatPickupWindowLineFromResponse(row),
    shipperContactLine: shipperContactFromResponse(row),
    consigneeName: sanitizeDisplayName(row.consignee?.name ?? ""),
    consigneeAddressLine: formatConsigneeAddressFromResponse(row),
    consigneeContactLine: consigneeContactFromResponse(row),
    itemLines: itemLinesFromResponse(row),
  };
}

function pickupGroupAggregateStatus(
  completed: number,
  total: number
): DispatchManifestStopDetailStatus {
  if (total === 0) return DispatchManifestStopDetailStatus.PENDING;
  if (completed === total) return DispatchManifestStopDetailStatus.COMPLETED;
  if (completed > 0) return DispatchManifestStopDetailStatus.IN_PROGRESS;
  return DispatchManifestStopDetailStatus.PENDING;
}

function buildPickupGroupsForDriver(
  driverId: number,
  rows: PickupsResponseContentModel[]
): PickupGroupBlock[] {
  if (rows.length === 0) return [];

  const map = new Map<string, PickupsResponseContentModel[]>();
  for (const row of rows) {
    const key = formatShipperAddress(row);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  }

  const groups: PickupGroupBlock[] = [];
  let idx = 0;
  for (const [address, list] of map.entries()) {
    const completed = list.filter((r) => r.pickup.pickupStatus === PickupStatus.COMPLETED).length;
    const total = list.length;
    const pickups = list.map(pickupToGroupRow);
    const safeKey = address.replace(/[^\w-]+/g, "-").slice(0, 48);
    const geo = list[0]?.shipper?.address?.geolocation;
    groups.push({
      kind: "pickup_group",
      id: `pickup-group-${driverId}-${idx}-${safeKey}`,
      shipperAddress: address,
      driverListId: `driver-${driverId}`,
      pickups,
      totalPickups: total,
      completedPickups: completed,
      groupStopStatus: pickupGroupAggregateStatus(completed, total),
      shipperLat: geo?.lat,
      shipperLng: geo?.lng,
    });
    idx += 1;
  }
  return groups;
}

function groupManifestsByDriver(manifests: DispatchManifest[]): Map<number, DispatchManifest[]> {
  const map = new Map<number, DispatchManifest[]>();
  for (const m of manifests) {
    const id = m.driverNumber;
    if (!map.has(id)) map.set(id, []);
    map.get(id)!.push(m);
  }
  for (const [, list] of map) {
    list.sort((a, b) => moment(b.dispatchedAt).valueOf() - moment(a.dispatchedAt).valueOf());
  }
  return map;
}

/** Yields so the browser can paint / stay responsive between heavy CPU chunks. */
function yieldToMainThread(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/** Builds one driver column group (extracted for chunked async assembly). */
function buildOneDriverGroup(
  detail: DriverDutyDetail,
  manifestsByDriver: Map<number, DispatchManifest[]>,
  pickupsByDriver: Map<number, PickupsResponseContentModel[]>
): PickupAssignmentDriverGroup {
  const driverId = detail.driverId;
  const mds = manifestsByDriver.get(driverId) ?? [];
  const primary = mds[0];

  const manifestBlocks: ManifestStopBlock[] = [];
  for (const manifest of mds) {
    for (const stop of manifest.stopDeatils ?? []) {
      const probills = probillRowsFromStop(stop.probills ?? [], manifest, stop.stopNumber);
      const address = stop.probills?.[0]?.consigneeAddress ?? "—";
      const deliveriesCount = probills.length;
      const stopProgressLabel = stopProgressFromProbills(probills);
      manifestBlocks.push({
        kind: "manifest_stop",
        id: `stop-${manifest.manifestNumber}-${stop.stopNumber}`,
        manifestNumber: manifest.manifestNumber,
        stopNumber: stop.stopNumber,
        address,
        stopStatus: stop.stopStatus,
        probills,
        deliveriesCount,
        stopProgressLabel,
      });
    }
  }

  const driverPickups = pickupsByDriver.get(driverId) ?? [];
  const pickupGroups = buildPickupGroupsForDriver(driverId, driverPickups);

  const stops: DriverColumnItem[] = [...manifestBlocks, ...pickupGroups];

  const deliveryProbillCount = manifestBlocks.reduce((n, b) => n + b.probills.length, 0);
  const deliveredProbillsCount = manifestBlocks.reduce(
    (n, b) => n + b.probills.filter((p) => p.probillStatus === ProbillStatus.DELIVERED).length,
    0
  );
  const completedPickupsCount = driverPickups.filter(
    (r) => r.pickup.pickupStatus === PickupStatus.COMPLETED
  ).length;

  const progressTotal = driverPickups.length + deliveryProbillCount;
  const progressDone = completedPickupsCount + deliveredProbillsCount;
  const progressLabel =
    progressTotal === 0 ? "—" : `${progressDone}/${progressTotal} done`;

  const loc = detail.driverLocation;
  const driverLocation =
    loc?.lat != null &&
    loc.lng != null &&
    Number.isFinite(Number(loc.lat)) &&
    Number.isFinite(Number(loc.lng))
      ? {
          lat: Number(loc.lat),
          lng: Number(loc.lng),
          formattedAddress: loc.formattedAddress,
          timestamp: loc.timestamp,
        }
      : null;

  return {
    id: `driver-${driverId}`,
    name: detail.driverName,
    driverId: String(driverId),
    stops,
    numberOfPickups: driverPickups.length,
    numberOfDeliveries: deliveryProbillCount,
    deliveriesCompleted: deliveredProbillsCount,
    pickupsCompleted: completedPickupsCount,
    trailerLabel: buildTrailerLabel(primary, detail),
    manifestLabel: buildManifestLabel(primary),
    driverStatus: mapDutyStatusToUi(detail),
    progressLabel,
    lastLocationLine: buildLastLocationLine(detail),
    primaryManifestNumber: primary?.manifestNumber ?? null,
    driverLocation,
  };
}

const DRIVERS_BUILD_BATCH = 5;

/**
 * Assembles driver groups in small batches with yields between batches so the tab
 * does not freeze for tens of seconds after the network returns (terminal switch).
 */
async function buildDriverGroupsAsync(
  dutyList: DriverDutyDetail[],
  manifests: DispatchManifest[],
  pickups: PickupsResponseContentModel[]
): Promise<PickupAssignmentDriverGroup[]> {
  const manifestsByDriver = groupManifestsByDriver(manifests);
  const pickupsByDriver = new Map<number, PickupsResponseContentModel[]>();
  for (const row of pickups) {
    const id = row.pickup.driverId;
    if (!pickupsByDriver.has(id)) pickupsByDriver.set(id, []);
    pickupsByDriver.get(id)!.push(row);
  }
  if (pickups.length > 4000) {
    await yieldToMainThread();
  }

  const out: PickupAssignmentDriverGroup[] = [];
  for (let i = 0; i < dutyList.length; i += DRIVERS_BUILD_BATCH) {
    const end = Math.min(i + DRIVERS_BUILD_BATCH, dutyList.length);
    for (let j = i; j < end; j++) {
      out.push(buildOneDriverGroup(dutyList[j], manifestsByDriver, pickupsByDriver));
    }
    if (end < dutyList.length) {
      await yieldToMainThread();
    }
  }
  return out;
}

async function fetchAllPickupsForAssignment(
  terminal: number,
  dateStr: string
): Promise<PickupsResponseContentModel[]> {
  const filter = {
    statuses: [PickupStatus.ASSIGNED, PickupStatus.COMPLETED],
    expiredOnly: false,
    showFuture: false,
    search: "",
    dateRange: { startDate: dateStr, endDate: dateStr },
  };

  const all: PickupsResponseContentModel[] = [];
  let lastIds: string | undefined;
  let page = 0;

  while (page < MAX_PICKUP_PAGES) {
    const res = await pickupService.getPickups(
      terminal,
      filter,
      lastIds ? { lastIds } : undefined
    );
    if (!res.ok()) {
      throw new Error(
        typeof res.getError === "function" ? String(res.getError()) : "Failed to load pickups"
      );
    }
    if (res.status === 204) break;

    const data = res.data;
    if (data?.content?.length) {
      all.push(...data.content);
    }

    const scroll = data?.scroll;
    const hasNext = scroll?.idsMap?.hasNext === "true";
    if (!hasNext || !scroll?.lastIds) break;
    lastIds = scroll.lastIds;
    page += 1;
  }

  return all;
}

function normalizeSearchToken(q: string): string {
  return q.trim().toLowerCase();
}

function fieldMatches(field: string | number | null | undefined, needle: string): boolean {
  if (needle === "") return false;
  if (field == null || field === "") return false;
  return String(field).toLowerCase().includes(needle);
}

function driverMatchesPrimarySearch(driver: PickupAssignmentDriverGroup, needle: string): boolean {
  if (needle === "") return false;
  const candidates = [
    driver.name,
    driver.driverId,
    driver.trailerLabel,
    driver.manifestLabel,
    driver.lastLocationLine,
    driver.progressLabel,
    driver.driverStatus,
    String(driver.numberOfPickups),
    String(driver.numberOfDeliveries),
    String(driver.deliveriesCompleted),
    String(driver.pickupsCompleted),
  ];
  return candidates.some((c) => fieldMatches(c, needle));
}

function manifestStopMatchesPrimary(stop: ManifestStopBlock, needle: string): boolean {
  if (needle === "") return true;
  if (fieldMatches(stop.address, needle)) return true;
  for (const pb of stop.probills) {
    if (fieldMatches(pb.consigneeName, needle) || fieldMatches(pb.probillNumber, needle)) {
      return true;
    }
  }
  return false;
}

function pickupGroupMatchesPrimary(stop: PickupGroupBlock, needle: string): boolean {
  if (needle === "") return true;
  if (fieldMatches(stop.shipperAddress, needle)) return true;
  return stop.pickups.some((p) => fieldMatches(p.shipperName, needle));
}

function stopMatchesPrimarySearch(stop: DriverColumnItem, needle: string): boolean {
  if (needle === "") return true;
  if (stop.kind === "manifest_stop") return manifestStopMatchesPrimary(stop, needle);
  return pickupGroupMatchesPrimary(stop, needle);
}

function manifestStopMatchesSecondary(stop: ManifestStopBlock, needle: string): boolean {
  if (needle === "") return true;
  return stop.probills.some((pb) => fieldMatches(pb.probillNumber, needle));
}

function pickupGroupMatchesSecondary(stop: PickupGroupBlock, needle: string): boolean {
  if (needle === "") return true;
  return stop.pickups.some(
    (p) =>
      fieldMatches(p.pickupNumber, needle) ||
      fieldMatches(p.probillNumber, needle) ||
      fieldMatches(p.bolNumber, needle) ||
      fieldMatches(p.poNumber, needle)
  );
}

function stopMatchesSecondarySearch(stop: DriverColumnItem, needle: string): boolean {
  if (needle === "") return true;
  if (stop.kind === "manifest_stop") return manifestStopMatchesSecondary(stop, needle);
  return pickupGroupMatchesSecondary(stop, needle);
}

const ONE_HOUR_MS = 60 * 60 * 1000;

export type DriverTabFilter = "all" | "atRisk" | "late";

function parsePickupMoment(pickupDate: string, timeStr: string): moment.Moment | null {
  if (!pickupDate?.trim() || !timeStr?.trim()) return null;
  const d = pickupDate.trim();
  const t = timeStr.trim();
  const formats = [
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD H:mm:ss",
    "YYYY-MM-DD H:mm",
  ];
  let m = moment(`${d} ${t}`, formats, true);
  if (!m.isValid()) {
    m = moment(`${d}T${t}`);
  }
  return m.isValid() ? m : null;
}

/**
 * Late: current time is after the pickup window end.
 * At risk: within 1 hour before window opens, or inside window with ≤1 hour until close.
 * Completed pickups are not at risk / late.
 */
export function classifyPickupTiming(
  pickupDate: string,
  readyTime: string,
  closeTime: string,
  pickupStatus: string,
  now: moment.Moment = moment()
): "late" | "at_risk" | "normal" {
  if (pickupStatus === PickupStatus.COMPLETED) return "normal";
  const readyAt = parsePickupMoment(pickupDate, readyTime);
  const closeAt = parsePickupMoment(pickupDate, closeTime);
  if (!readyAt || !closeAt) return "normal";
  const endAt =
    closeAt.isBefore(readyAt) || closeAt.isSame(readyAt, "minute")
      ? closeAt.clone().add(1, "day")
      : closeAt.clone();
  if (now.isAfter(endAt)) return "late";
  if (now.isBefore(readyAt)) {
    const msToReady = readyAt.diff(now);
    if (msToReady > 0 && msToReady <= ONE_HOUR_MS) return "at_risk";
    return "normal";
  }
  const msToClose = endAt.diff(now);
  if (msToClose > 0 && msToClose <= ONE_HOUR_MS) return "at_risk";
  return "normal";
}

function filterPickupGroupForTab(
  stop: PickupGroupBlock,
  tab: Exclude<DriverTabFilter, "all">
): PickupGroupBlock | null {
  const want = tab === "atRisk" ? "at_risk" : "late";
  const filtered = stop.pickups.filter(
    (p) =>
      classifyPickupTiming(p.pickupDate, p.readyTime, p.closeTime, p.status) === want
  );
  if (filtered.length === 0) return null;
  const completed = filtered.filter((r) => r.status === PickupStatus.COMPLETED).length;
  return {
    ...stop,
    pickups: filtered,
    totalPickups: filtered.length,
    completedPickups: completed,
    groupStopStatus: pickupGroupAggregateStatus(completed, filtered.length),
  };
}

function recomputeDriverAggregates(
  driver: PickupAssignmentDriverGroup,
  stops: DriverColumnItem[]
): PickupAssignmentDriverGroup {
  let numberOfPickups = 0;
  let pickupsCompleted = 0;
  let numberOfDeliveries = 0;
  let deliveriesCompleted = 0;
  for (const s of stops) {
    if (s.kind === "manifest_stop") {
      numberOfDeliveries += s.probills.length;
      deliveriesCompleted += s.probills.filter(
        (pb) => pb.probillStatus === ProbillStatus.DELIVERED
      ).length;
    } else {
      numberOfPickups += s.totalPickups;
      pickupsCompleted += s.completedPickups;
    }
  }
  const progressTotal = numberOfPickups + numberOfDeliveries;
  const progressDone = pickupsCompleted + deliveriesCompleted;
  const progressLabel =
    progressTotal === 0 ? "—" : `${progressDone}/${progressTotal} done`;
  return {
    ...driver,
    stops,
    numberOfPickups,
    pickupsCompleted,
    numberOfDeliveries,
    deliveriesCompleted,
    progressLabel,
  };
}

function filterDriverForTab(
  driver: PickupAssignmentDriverGroup,
  tab: DriverTabFilter
): PickupAssignmentDriverGroup | null {
  if (tab === "all") return driver;
  const nextStops: DriverColumnItem[] = [];
  for (const stop of driver.stops) {
    if (stop.kind === "manifest_stop") continue;
    const filtered = filterPickupGroupForTab(stop, tab);
    if (filtered) nextStops.push(filtered);
  }
  if (nextStops.length === 0) return null;
  return recomputeDriverAggregates(driver, nextStops);
}

/** At Risk / Late: only pickup groups with matching scheduled pickups; manifest-only rows are omitted on those tabs. */
export function filterDriverGroupsByTab(
  drivers: PickupAssignmentDriverGroup[],
  tab: DriverTabFilter
): PickupAssignmentDriverGroup[] {
  if (tab === "all") return drivers;
  return drivers
    .map((d) => filterDriverForTab(d, tab))
    .filter((d): d is PickupAssignmentDriverGroup => d != null);
}

/** Client-side filter for the pickup assignment Drivers column search fields. */
export function filterDriverGroupsBySearch(
  drivers: PickupAssignmentDriverGroup[],
  primaryRaw: string,
  secondaryRaw: string
): PickupAssignmentDriverGroup[] {
  const primary = normalizeSearchToken(primaryRaw);
  const secondary = normalizeSearchToken(secondaryRaw);
  const primaryActive = primary.length > 0;
  const secondaryActive = secondary.length > 0;

  if (!primaryActive && !secondaryActive) {
    return drivers;
  }

  return drivers
    .map((driver) => {
      const { stops } = driver;
      const filteredStops = stops.filter(
        (s) =>
          stopMatchesPrimarySearch(s, primary) && stopMatchesSecondarySearch(s, secondary)
      );

      const driverHeaderOnlyMatch =
        primaryActive &&
        !secondaryActive &&
        driverMatchesPrimarySearch(driver, primary) &&
        filteredStops.length === 0;

      if (driverHeaderOnlyMatch) {
        return { ...driver, stops: [...stops] };
      }

      if (filteredStops.length === 0) {
        return null;
      }

      return { ...driver, stops: filteredStops };
    })
    .filter((d): d is PickupAssignmentDriverGroup => d != null);
}

async function loadDriversColumnData(terminalNumber: number, dispatchedDate: string) {
  const [dutyRes, manifestRes, pickups] = await Promise.all([
    driverService.getDriversDutyDetails({ terminalNumber }),
    dispatchBoardService.getDispatcedManifestsDetails({
      terminalNumber,
      dispatchedDate,
    }),
    fetchAllPickupsForAssignment(terminalNumber, dispatchedDate),
  ]);

  if (!dutyRes.ok()) {
    throw new Error(
      typeof dutyRes.getError === "function" ? String(dutyRes.getError()) : "Failed to load drivers"
    );
  }
  if (!manifestRes.ok()) {
    throw new Error(
      typeof manifestRes.getError === "function"
        ? String(manifestRes.getError())
        : "Failed to load manifests"
    );
  }

  const dutyRaw = dutyRes.data as unknown;
  const dutyList: DriverDutyDetail[] = Array.isArray(dutyRaw)
    ? dutyRaw
    : [];

  const manifests = manifestRes.data?.manifests ?? [];

  return buildDriverGroupsAsync(dutyList, manifests, pickups);
}

/** Stable fallback when `query.data` is undefined so consumers' useEffect deps don't change every render. */
const EMPTY_DRIVERS: PickupAssignmentDriverGroup[] = [];

export function usePickupAssignmentDriversColumn(terminalNumber: number | undefined) {
  const dispatchedDate = moment().format("YYYY-MM-DD");

  const query = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_ASSIGNMENT_DRIVERS_COLUMN, terminalNumber, dispatchedDate],
    queryFn: () => loadDriversColumnData(terminalNumber!, dispatchedDate),
    enabled: terminalNumber != null && terminalNumber > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    drivers: query.data ?? EMPTY_DRIVERS,
    loading: query.isLoading || query.isFetching,
    error: query.error instanceof Error ? query.error.message : query.error ? String(query.error) : null,
    refetch: query.refetch,
  };
}
